import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import * as md5 from 'md5';

import { Observable } from 'rxjs/Observable';
import { OAuthService } from 'angular-oauth2-oidc';
import { isString, isObject } from 'util';
import { ConfigService } from '@ngx-config/core';
import { Store } from '@ngrx/store';
import { has } from 'lodash';

import * as appSelectors from '../../../shared/state-management/reducers';

import { environment } from '../../../../environments/environment';
import { WebstartService } from '../../../shared/webstart/webstart.service';
import { ValidationResponseModel } from '../shared/models/validation-response.model';
import { WebserviceService } from '../shared/services/webservice/webservice.service';
import { WindowRef } from '../../../shared/services/window-ref';
import { TranslateService } from '../../../shared/i18n/services/translate.service';
import { CrsBridgeService } from '../../../shared/services/crs-bridge/crs-bridge.service';
import { Webstart } from '../../../shared/webstart/models/webstart-response';
import { WebsocketService } from '../../../shared/services/websocket/websocket.service';
import { KeyValueObject } from '../../../shared/models/key-value-object';



@Injectable()
export class CrsService {
	private _smartBookingWindows: KeyValueObject<Window|null> = {};

	constructor(
		private _ws: WebserviceService,
		private _http: HttpClient,
		private _crsBridge: CrsBridgeService,
		private _window: WindowRef,
		private _oauthService: OAuthService,
		private _config: ConfigService,
		private _store: Store<appSelectors.State>,
		private _translateService: TranslateService,
		private _webstartService: WebstartService,
		private _websocket: WebsocketService,
	) {}

	public sendToCrs(crs: string, xml: string, data?: ValidationResponseModel): Observable<string> {
		if (isString(xml) && xml.length) {
			this._window.nativeWindow.postMessage(JSON.stringify({
				type: 'vanessa:crs-presend:' + this._getCrsForApplication(crs),
				xml: encodeURIComponent(xml),
			}), '*');

			return Observable.of(xml);
		}

		return this.getXmlForCrs(crs, data)
			.take(1)
			.do(tempXML => {
				this._window.nativeWindow.postMessage(JSON.stringify({
					type: 'vanessa:crs-presend:' + this._getCrsForApplication(crs),
					xml: encodeURIComponent(tempXML),
				}), '*');
			});
	}

	public sendToBridge(crs: string, xml: string): Observable<string> {
		return this._crsBridge.sendToCrs(crs, xml);
	}

	public sendViaSocket(crs: string, xml: string) {
		const body = `xml=${encodeURIComponent(xml)}`;

		return this._http.post('https://proxy.schmetterling-argus.de/neo/import/', body, {
			responseType: 'text',
			headers: new HttpHeaders({
				Authorization: `Bearer ${this._oauthService.getAccessToken()}`,
				'Content-Type': 'application/x-www-form-urlencoded'
			})
		});
	}

	public postToNewWebstartTab(webstartIdentifier: string, crs: string, data: ValidationResponseModel) {
		let agentId: string = '';
		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => agentId = `${agency.agentId}`);

		let webstartUrl = '';

		return this._webstartService.getCachedWebstartByIdentifier(webstartIdentifier)
			.take(1)
			.flatMap(webstarts => webstarts)
			.filter((webstart) => webstart.agentId === agentId)
			.do(ws => webstartUrl = ws.url)
			.switchMap(ws => this.getXmlForCrs(crs, data))
			.subscribe(xml => {
				const frag = document.createDocumentFragment();
				const form = document.createElement('form');

				form.action = webstartUrl;
				form.target = '_blank';
				form.method = 'post';

				const input = document.createElement('input');

				input.type = 'hidden';
				input.name = 'xml';
				input.value = xml;

				form.appendChild(input);
				frag.appendChild(form);

				document.body.appendChild(frag);

				form.submit();

				document.body.removeChild(form);
			});
	}

	public sendViaWebstart(webstartIdentifier: string, crs: string, data: ValidationResponseModel) {
		let agentId: string = '';
		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => agentId = `${agency.agentId}`);

		let webstartUrl = '';

		return this._webstartService.getCachedWebstartByIdentifier(webstartIdentifier)
			.take(1)
			.flatMap(webstarts => webstarts)
			.filter((webstart) => webstart.agentId === agentId)
			.do(ws => webstartUrl = ws.url)
			.switchMap(ws => this.getXmlForCrs(crs, data))
			.map(xml => `data=${encodeURIComponent(xml)}`)
			.switchMap(postBody => {
				return this._http.post<{url?: string, errors?: string[], status?: string, message?: string}>( webstartUrl, postBody, {
					responseType: 'json',
					headers: new HttpHeaders({'Content-Type': 'application/x-www-form-urlencoded'})
				});
			})
			.map(response => {
				if (response.errors) {
					throw new Error(response.errors.join(', '));
				}

				if (response.status && response.status === 'error') {
					throw new Error(response.message || `Error ${response.status}`);
				}

				return response.url;
			});
	}


	public setRentalCarStations(
		offerId: string,
		pickUpStationId: string,
		dropOffStationId: string
	): Observable<any> {
		const baseUrl: string = this._config.getSettings('xena').rentalCar.endpoint + 'select/';
		const url: string = baseUrl + offerId + '?pickUpStationId=' + pickUpStationId + '&dropOffStationId=' + dropOffStationId;
		const userId: string = `${this._oauthService.getIdentityClaims()['user']['id']}`;
		let agentId: string = '';

		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => agentId = `${agency.agentId}`);

		return this._http.get(url, { headers: new HttpHeaders({
			'AGENT': agentId,
			'USER': userId,
			'TOKEN': this._getRentalCarApiToken(agentId)
		})});
	}

	public getRentalCarXmlForCrs(offerId: string): Observable<string> {
		const url: string = this._config.getSettings('xena').rentalCar.endpoint + 'crs/' + offerId;
		const userId: string = `${this._oauthService.getIdentityClaims()['user']['id']}`;
		let agentId: string = '';

		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => agentId = `${agency.agentId}`);

		return this._http.get(url, { responseType: 'text', headers: new HttpHeaders({
			'AGENT': agentId,
			'USER': userId,
			'TOKEN': this._getRentalCarApiToken(agentId)
		})});
	}

	public openPaxLounge(data: ValidationResponseModel, searchMaskUrl: string, searchMaskId: number) {
		this._ws.crs(data.token, 'PAX', searchMaskUrl, searchMaskId).take(1).subscribe(res => {
			if (isObject(res)) {
				const paxWindow = window.open(res['location'], '_blank');

				paxWindow.document.write(`<p>${this._translateService.translate('paxconnect:export:tab-closing-message')}</p>`);

				setTimeout(() => {
					paxWindow.close();
				}, 15000);
			}
		});
	}

	public getSmartBookingBetaVersion(): string|null {
		if (false && (window.location.href.indexOf('localhost') !== -1 || window.location.href.indexOf('127.0.0.1') !== -1)) {
			return 'local';
		}

		return null;
	}

	public openSmartBooking(webstart: Webstart): void {
		const smartBookingWindow = this._smartBookingWindows[webstart.agentId];
		const betaVersion: string = this.getSmartBookingBetaVersion();

		if (smartBookingWindow && has(smartBookingWindow, 'closed') && !smartBookingWindow.closed) {
			smartBookingWindow.focus();
			return;
		}

		this._smartBookingWindows[webstart.agentId] = window.open(webstart.url + (betaVersion ? `?version=${betaVersion}` : ''), '_blank');
	}

	public sendToSmartBooking(webstart: Webstart, data): void {
		const smartBookingWindow = this._smartBookingWindows[webstart.agentId];

		if (smartBookingWindow && has(smartBookingWindow, 'closed') && !smartBookingWindow.closed) {
			this._websocket.sendToSmartBooking(data);
			smartBookingWindow.focus();
			return;
		}

		this._smartBookingWindows[webstart.agentId] = window.open('', '_blank');

		const form = this._smartBookingWindows[webstart.agentId].document.createElement('form');
		const payloadInput = this._smartBookingWindows[webstart.agentId].document.createElement('input');
		const betaVersion: string = this.getSmartBookingBetaVersion();

		payloadInput.type = 'hidden';
		payloadInput.name = 'payload';
		payloadInput.setAttribute('value', JSON.stringify(data));
		form.target = '_self';
		form.method = 'POST';
		form.style.display = 'none';
		form.action = webstart.url + (betaVersion ? `?version=${betaVersion}` : '');
		form.appendChild(payloadInput);
		this._smartBookingWindows[webstart.agentId].document.body.appendChild(form);
		form.submit();
		this._smartBookingWindows[webstart.agentId].document.body.removeChild(form);
	}

	public getXmlForCrs(crs: string, data: ValidationResponseModel): Observable<string> {
		if (!isObject(data) || !isString(data.token) || !(data.token.length)) {
			return Observable.of('');
		}

		return this._ws.crs(data.token, crs)
			.map(res => this.prepareXmlForCrs(crs, res.crs));
	}

	public prepareXmlForCrs(crs: string, xml: string): string {
		return (xml || '').replace(/application="[a-zA-Z0-9\s]+"/im, 'application="' + this._getCrsForApplication(crs) + '"');
	}

	private _getRentalCarApiToken(agentId: string): string {
		return md5(environment.xena.rentalCar.token + agentId);
	}

	private  _getCrsForApplication(crs): string {
		let returnValue = crs;

		if (crs.toUpperCase() === 'MERLIN') {
			returnValue = 'Merlin';
		}

		if (crs.toUpperCase() === 'IRIS') {
			returnValue = 'IrisPlus';
		}

		if (crs.toUpperCase() === 'AMADEUS') {
			returnValue = 'Amadeus';
		}

		if (crs.toUpperCase() === 'LEAFLET') {
			returnValue = 'neo';
		}

		if (crs.toUpperCase() === 'URLAUBSIDEE') {
			returnValue = 'neo';
		}

		return returnValue;
	}
}



// WEBPACK FOOTER //
// ./src/app/xena/core/crs/crs.service.ts